import { useState, useEffect } from 'react';
import { getTickets, createTicket, updateTicket, deleteTicket } from '../services/event.service';
import { Ticket } from '../types/event';
import { useToast } from '../hooks/useToast';

export const useTickets = (eventId: string) => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const { showToast } = useToast();

  useEffect(() => {
    const fetchTickets = async () => {
      if (!eventId) return;
      
      try {
        setLoading(true);
        const data = await getTickets(eventId);
        setTickets(data);
        setError(null);
      } catch (err: any) {
        setError(err);
        showToast('Erreur lors du chargement des tickets', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchTickets();
  }, [eventId]);

  const addTicket = async (ticket: Partial<Ticket>) => {
    try {
      setLoading(true);
      const newTicket = await createTicket(ticket);
      setTickets(prev => [...prev, newTicket]);
      showToast('Ticket créé avec succès', 'success');
      return newTicket;
    } catch (err: any) {
      setError(err);
      showToast('Erreur lors de la création du ticket', 'error');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const editTicket = async (id: string, ticket: Partial<Ticket>) => {
    try {
      setLoading(true);
      const updatedTicket = await updateTicket(id, ticket);
      setTickets(prev => prev.map(t => t.id === id ? updatedTicket : t));
      showToast('Ticket mis à jour avec succès', 'success');
      return updatedTicket;
    } catch (err: any) {
      setError(err);
      showToast('Erreur lors de la mise à jour du ticket', 'error');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const removeTicket = async (id: string) => {
    try {
      setLoading(true);
      await deleteTicket(id);
      setTickets(prev => prev.filter(t => t.id !== id));
      showToast('Ticket supprimé avec succès', 'success');
      return true;
    } catch (err: any) {
      setError(err);
      showToast('Erreur lors de la suppression du ticket', 'error');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { tickets, loading, error, addTicket, editTicket, removeTicket };
};
